import { Link, useLocation, useNavigate } from '@tanstack/react-router';
import { motion, AnimatePresence } from 'motion/react';
import {
  X,
  User,
  LogOut,
  ChevronDown,
  Briefcase,
  Shield,
  MapPin,
  DollarSign,
  Map,
  Menu,
} from 'lucide-react';
import { useState, useRef, useEffect, useCallback, lazy, Suspense } from 'react';

import { ThemeToggle } from '@/components/theme-toggle';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { useUserProfile } from '@/hooks/useUserProfile';

// Lazy load AuthModal to match the pattern in index.tsx
const AuthModal = lazy(() => import('@/components/auth/AuthModal'));

const navLinks = [
  { path: '/', label: 'Home' },
  { path: '/#builder', label: 'About' },
  { path: '/#contact', label: 'Contact' },
];

const adminLinks = [
  { path: '/sitemap', label: 'Sitemap', icon: Map },
  { path: '/admin/destinations', label: 'Manage Destinations', icon: MapPin },
  { path: '/admin/affiliates', label: 'Affiliates', icon: DollarSign },
];

const userMenuItems = [
  { path: '/profile', label: 'Profile', icon: User },
  { path: '/trips', label: 'My Trips', icon: Briefcase },
];

export function SiteNav() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const { currentUser, signOut, loading } = useAuth();
  const { profile } = useUserProfile();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const navigate = useNavigate();

  const smoothScrollTo = useCallback((targetY: number, onComplete?: () => void) => {
    const startY = window.scrollY;
    const distance = targetY - startY;
    if (distance === 0) return;
    const duration = 1200; // ms
    let startTime: number | null = null;

    function easeInOutCubic(t: number) {
      return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
    }

    function step(timestamp: number) {
      if (!startTime) startTime = timestamp;
      const elapsed = timestamp - startTime;
      const progress = Math.min(elapsed / duration, 1);
      window.scrollTo(0, startY + distance * easeInOutCubic(progress));
      if (progress < 1) {
        requestAnimationFrame(step);
      } else {
        onComplete?.();
      }
    }

    requestAnimationFrame(step);
  }, []);

  const scrollToHash = useCallback((hash: string) => {
    const el = document.getElementById(hash);
    if (!el) return;

    const navbarHeight = 64;
    const targetY = el.getBoundingClientRect().top + window.scrollY - navbarHeight;
    smoothScrollTo(targetY, () => {
      window.history.replaceState(null, '', `/#${hash}`);
    });
  }, [smoothScrollTo]);

  const scrollToTop = useCallback(() => {
    smoothScrollTo(0);
  }, [smoothScrollTo]);

  const handleHomeClick = useCallback(
    (e: React.MouseEvent) => {
      if (location.pathname === '/') {
        e.preventDefault();
        scrollToTop();
      }
      // If on another page, let the Link navigate normally — scroll to top happens on mount
    },
    [location.pathname, scrollToTop]
  );

  const handleHashLink = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>, path: string) => {
      e.preventDefault();
      const hash = path.split('#')[1];
      if (!hash) return;

      if (location.pathname === '/') {
        scrollToHash(hash);
      } else {
        navigate({ to: '/' });
        // Wait for navigation and DOM to settle before scrolling
        setTimeout(() => scrollToHash(hash), 100);
      }
    },
    [location.pathname, navigate, scrollToHash]
  );

  const handleSignOut = async () => {
    try {
      await signOut();
      setUserDropdownOpen(false);
    } catch (error) {
      console.error('Sign out error:', error);
    }
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setUserDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <>
      <nav className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Link
                  to="/"
                  className="block"
                  onClick={handleHomeClick}
                >
                  <h1 className="font-display text-2xl font-black text-foreground tracking-tight">
                    W<span className="text-terra">u</span>ndra
                  </h1>
                </Link>
              </div>
              <div className="hidden md:flex items-center ml-10 space-x-8">
                {navLinks.map(link =>
                  link.path.startsWith('/#') ? (
                    <a
                      key={link.path}
                      href={link.path}
                      onClick={(e) => handleHashLink(e, link.path)}
                      className="text-muted-foreground hover:text-terra px-3 py-2 rounded-md text-sm font-medium transition-colors cursor-pointer"
                    >
                      {link.label}
                    </a>
                  ) : (
                    <Link
                      key={link.path}
                      to={link.path}
                      search={{}}
                      className="text-muted-foreground hover:text-terra px-3 py-2 rounded-md text-sm font-medium transition-colors [&.active]:text-foreground [&.active]:font-semibold"
                      onClick={link.path === '/' ? handleHomeClick : undefined}
                    >
                      {link.label}
                    </Link>
                  )
                )}

                {/* Affiliate Link - Only show when user is an affiliate */}
                {currentUser && profile?.isAffiliate && (
                  <Link
                    to="/affiliate/dashboard"
                    search={{}}
                    className="text-muted-foreground hover:text-foreground px-3 py-2 rounded-md text-sm font-medium transition-colors [&.active]:text-foreground [&.active]:font-semibold hover:bg-terra/10"
                  >
                    Affiliate Dashboard
                  </Link>
                )}

              </div>
            </div>

            <div className="flex items-center space-x-2">
              <ThemeToggle />

              {/* Authentication Section */}
              {!loading && (
                <div className="hidden md:flex items-center space-x-2">
                  {currentUser ? (
                    // Authenticated user dropdown
                    <div className="relative" ref={dropdownRef}>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setUserDropdownOpen(!userDropdownOpen)}
                        className="flex items-center space-x-2 px-3 py-2 text-muted-foreground hover:text-foreground"
                      >
                        <User className="h-4 w-4" />
                        <span className="text-sm font-medium">
                          {currentUser.displayName || currentUser.email}
                        </span>
                        <ChevronDown
                          className={`h-4 w-4 transition-transform duration-200 ${
                            userDropdownOpen ? 'rotate-180' : ''
                          }`}
                        />
                      </Button>

                      <AnimatePresence>
                        {userDropdownOpen && (
                          <motion.div
                            initial={{ opacity: 0, scale: 0.95, y: -10 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.95, y: -10 }}
                            transition={{ duration: 0.2 }}
                            className="absolute right-0 mt-2 w-56 bg-background border rounded-md shadow-lg z-50"
                          >
                            <div className="py-1">
                              {userMenuItems.map(item => (
                                <Link
                                  key={item.path}
                                  to={item.path}
                                  search={
                                    item.path === '/profile'
                                      ? { tab: 'profile' }
                                      : {}
                                  }
                                  className="flex items-center space-x-2 px-4 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-terra/10 transition-colors"
                                  onClick={() => setUserDropdownOpen(false)}
                                >
                                  <item.icon className="h-4 w-4" />
                                  <span>{item.label}</span>
                                </Link>
                              ))}
                              {/* Admin links - only for admin users */}
                              {profile?.isAdmin && (
                                <>
                                  <div className="border-t my-1" />
                                  <div className="px-4 py-1.5 text-xs font-semibold text-terra uppercase tracking-wider flex items-center space-x-1">
                                    <Shield className="h-3 w-3" />
                                    <span>Admin</span>
                                  </div>
                                  {adminLinks.map(link => (
                                    <Link
                                      key={link.path}
                                      to={link.path}
                                      search={{}}
                                      className="flex items-center space-x-2 px-4 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-terra/10 transition-colors"
                                      onClick={() => setUserDropdownOpen(false)}
                                    >
                                      <link.icon className="h-4 w-4" />
                                      <span>{link.label}</span>
                                    </Link>
                                  ))}
                                </>
                              )}
                              <div className="border-t my-1" />
                              <button
                                onClick={handleSignOut}
                                className="flex items-center space-x-2 w-full px-4 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-terra/10 transition-colors text-left"
                              >
                                <LogOut className="h-4 w-4" />
                                <span>Sign Out</span>
                              </button>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  ) : (
                    // Unauthenticated user
                    <Button
                      variant="default"
                      size="sm"
                      onClick={() => setAuthModalOpen(true)}
                    >
                      Sign In
                    </Button>
                  )}
                </div>
              )}

              <div className="md:hidden">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                  aria-label="Toggle menu"
                >
                  {mobileMenuOpen ? (
                    <X className="h-5 w-5" />
                  ) : (
                    <Menu className="h-5 w-5" />
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              className="md:hidden"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
            >
              <div className="px-2 pt-2 pb-3 space-y-1 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b">
                {navLinks.map(link =>
                  link.path.startsWith('/#') ? (
                    <a
                      key={link.path}
                      href={link.path}
                      onClick={(e) => {
                        handleHashLink(e, link.path);
                        setMobileMenuOpen(false);
                      }}
                      className="text-foreground hover:bg-terra/10 block px-3 py-2 rounded-md text-base font-medium transition-colors cursor-pointer"
                    >
                      {link.label}
                    </a>
                  ) : (
                    <Link
                      key={link.path}
                      to={link.path}
                      search={{}}
                      className="text-foreground hover:bg-terra/10 block px-3 py-2 rounded-md text-base font-medium transition-colors [&.active]:bg-terra/10"
                      onClick={(e) => {
                        if (link.path === '/') handleHomeClick(e);
                        setMobileMenuOpen(false);
                      }}
                    >
                      {link.label}
                    </Link>
                  )
                )}

                {/* Affiliate Link in Mobile - Only show when user is an affiliate */}
                {currentUser && profile?.isAffiliate && (
                  <Link
                    to="/affiliate/dashboard"
                    search={{}}
                    className="text-foreground hover:bg-terra/10 block px-3 py-2 rounded-md text-base font-medium transition-colors [&.active]:bg-terra/10"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Affiliate Dashboard
                  </Link>
                )}

                {/* Mobile User Menu */}
                {currentUser &&
                  userMenuItems.map(item => (
                    <Link
                      key={item.path}
                      to={item.path}
                      search={{}}
                      className="text-foreground hover:bg-terra/10 block px-3 py-2 rounded-md text-base font-medium transition-colors [&.active]:bg-terra/10"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {item.label}
                    </Link>
                  ))}

                {/* Admin Links in Mobile - Only show when authenticated as admin */}
                {currentUser && profile?.isAdmin && (
                  <>
                    <div className="px-3 py-2 text-xs font-semibold text-terra uppercase tracking-wider flex items-center space-x-1">
                      <Shield className="h-3 w-3" />
                      <span>Admin</span>
                    </div>
                    {adminLinks.map(link => (
                      <Link
                        key={link.path}
                        to={link.path}
                        search={{}}
                        className="flex items-center space-x-2 text-foreground hover:bg-terra/10 px-3 py-2 pl-6 rounded-md text-base font-medium transition-colors [&.active]:bg-terra/10"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        <link.icon className="h-4 w-4" />
                        <span>{link.label}</span>
                      </Link>
                    ))}
                  </>
                )}

                {/* Mobile Authentication */}
                {!loading && (
                  <div className="border-t pt-2 mt-2">
                    {currentUser ? (
                      <div className="space-y-2">
                        <div className="px-3 py-2 text-sm text-muted-foreground">
                          Signed in as{' '}
                          {currentUser.displayName || currentUser.email}
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            handleSignOut();
                            setMobileMenuOpen(false);
                          }}
                          className="w-full justify-start"
                        >
                          <LogOut className="h-4 w-4 mr-2" />
                          Sign Out
                        </Button>
                      </div>
                    ) : (
                      <Button
                        variant="default"
                        size="sm"
                        onClick={() => {
                          setAuthModalOpen(true);
                          setMobileMenuOpen(false);
                        }}
                        className="w-full"
                      >
                        Sign In
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>

      {/* Authentication Modal */}
      <Suspense fallback={null}>
        <AuthModal
          isOpen={authModalOpen}
          onClose={() => setAuthModalOpen(false)}
        />
      </Suspense>
    </>
  );
}
